import type { ReactElement } from "react"
import { useContext } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { HighlightAlt, Timeline } from "@mui/icons-material"
import BatchPredictionOutlinedIcon from "@mui/icons-material/BatchPredictionOutlined"
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction"
import { CardActionArea, Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"

import type { RootState } from "../../../store/ReduxStore"
import { getEnvVar, isEnvVariableTruthy } from "../../../utils/genericHelpers"
import { Context } from "../Context"

import styles from "./PredictionsTypeSelection.module.scss"

/**
 * PredictionsTypeSelection Component
 *
 * Renders a selection interface for choosing between manual prediction and Test
 * and bulk file upload prediction and Test types. The component displays two
 * clickable cards with different styling and icons for each option.
 *
 * @returns {ReactElement} A Grid container with two CardActionArea components
 */
export function PredictionsTypeSelection(): ReactElement {
  const { setType, step, setStep, isTestModal } = useContext(Context)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const { t } = useTranslation("predictions", {
    keyPrefix: "predictionsTypeSelection",
  })

  const isBulkCreateEnabled = isEnvVariableTruthy(String(getEnvVar("PREDICTIONS_BULK_CREATE_ENABLED")))

  const manualPredictionConfig = {
    icon: isTestModal ? Timeline : OnlinePredictionIcon,
    iconClassName: styles.manualPredictionIcon,
    title: isTestModal
      ? currentProject?.category === "model"
        ? t("fullModelTestCard.title")
        : t("fullWorkflowTestCard.title")
      : currentProject?.category === "model"
        ? t("fullModelPredictionCard.title")
        : t("fullWorkflowPredictionCard.title"),
    description: isTestModal
      ? currentProject?.category === "model"
        ? t("fullModelTestCard.description")
        : t("fullWorkflowTestCard.description")
      : currentProject?.category === "model"
        ? t("fullModelPredictionCard.description")
        : t("fullWorkflowPredictionCard.description"),
  }

  const componentTestConfig = {
    icon: HighlightAlt,
    iconClassName: styles.componentTestIcon,
    title: t("componentTestCard.title"),
    description: t("componentTestCard.description"),
  }

  return (
    <Grid container gap={2} height={"fit-content"}>
      {/* Manual Prediction and Test Card */}
      <CardActionArea
        className={styles.blueCard}
        onClick={() => {
          setType?.("manual")
          setStep?.(step + 1)
        }}
      >
        {/* Manual prediction and Test icon with blue styling */}
        <manualPredictionConfig.icon className={manualPredictionConfig.iconClassName} />

        <Typography variant={"a"} color="important" variantColor={2}>
          {manualPredictionConfig.title}
        </Typography>

        <Typography variant={"p"} color="important" variantColor={2} align="center">
          {manualPredictionConfig.description}
        </Typography>
      </CardActionArea>

      {/* Bulk Upload Card */}
      {isBulkCreateEnabled && !isTestModal && (
        <CardActionArea
          className={styles.greenCard}
          onClick={() => {
            setType?.("bulk")
            setStep?.(step + 1)
          }}
        >
          {/* Batch prediction and Test icon with green styling */}
          <BatchPredictionOutlinedIcon className={styles.batchPredictionIcon} />

          <Typography variant={"a"} color="positive" variantColor={2}>
            {t("bulkUploadCard.title")}
          </Typography>

          <Typography variant={"p"} color="positive" variantColor={2} align="center">
            {t("bulkUploadCard.description")}
          </Typography>
        </CardActionArea>
      )}
      {/* single component testing */}
      {isTestModal && currentProject?.category === "workflow" && (
        <CardActionArea
          className={styles.purpleCard}
          onClick={() => {
            setType?.("component")
            setStep?.(step + 1)
          }}
        >
          {/* Manual prediction and Test icon with blue styling */}
          <componentTestConfig.icon className={componentTestConfig.iconClassName} />
          <Typography variant={"a"} color="purple" variantColor={2}>
            {componentTestConfig.title}
          </Typography>

          <Typography variant={"p"} color="base" variantColor={2} align="center">
            {componentTestConfig.description}
          </Typography>
        </CardActionArea>
      )}
    </Grid>
  )
}
